import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";
import { useI18n } from "./i18n";

const CMSCtx = createContext({ blocks: null, refresh: () => {} });

export function CMSProvider({ children }) {
  const [blocks, setBlocks] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/content");
      setBlocks(data);
    } catch (error) {
      console.warn("CMS: failed to load content blocks", error);
      setBlocks({});
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(() => ({ blocks, refresh }), [blocks, refresh]);

  return <CMSCtx.Provider value={value}>{children}</CMSCtx.Provider>;
}

export function useCMS() {
  return useContext(CMSCtx);
}

/** Returns the CMS-overridable text for a key (es/en aware), or falls back to the i18n key. */
export function useCText(key, fallbackKey) {
  const { blocks } = useCMS();
  const { lang, t } = useI18n();
  if (blocks && blocks[key]) {
    const v = blocks[key][lang] || blocks[key].es || "";
    if (v) return v;
  }
  return t(fallbackKey || key);
}

/**
 * Raw CMS value for settings like URLs, emails or images: the Spanish value (or the English one
 * when it exists and the site is in English), else `fallback`. Unlike useCText it never returns
 * the key name, so an empty field stays empty.
 */
export function useCValue(key, fallback = "") {
  const { blocks } = useCMS();
  const { lang } = useI18n();
  const b = blocks?.[key];
  if (!b) return fallback;
  return (lang === "en" && b.en) || b.es || fallback;
}
